import { useFlags } from '@features/feature-flags';
import {
    Button,
    ComingSoonPanel,
    IconArchive,
    IconAudit,
    IconFlag,
    IconPlus,
    IconTrendUp,
    PageHeader,
    StatTile,
} from '@shared/ui';
import { useMemo } from 'react';
import { Link } from 'react-router';

import type { FlagLifecycle, FlagListItem } from '@features/feature-flags';

/**
 * Overview. Every number on this screen is derived from the live flags catalog
 * — the same query the flags screen and the sidebar badge share, so the three
 * can never disagree. The mock's tiles (client orgs, add-on subscriptions,
 * 7-day change count) are gone rather than frozen at invented values: each
 * returns when the API it needs exists.
 */
export function OverviewPage() {
    const flags = useFlags();
    const rows = flags.data;

    const stats = useMemo(() => {
        if (!rows) return undefined;
        return {
            total: rows.length,
            enabled: rows.filter((row) => row.default.enabled && !row.default.archived).length,
            gated: rows.filter((row) => row.default.rolloutPercentage !== undefined).length,
            archived: rows.filter((row) => row.default.archived).length,
            beta: countLifecycle(rows, 'beta'),
            dev: countLifecycle(rows, 'dev'),
        };
    }, [rows]);

    return (
        <>
            <PageHeader
                eyebrow="Backoffice"
                title="Overview"
                description="Control plane for feature flags, add-ons, and client-org configuration across every Veevart tenant."
                actions={
                    <Link to="/flags">
                        <Button variant="primary">
                            <IconPlus className="size-4" />
                            New flag
                        </Button>
                    </Link>
                }
            />

            <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatTile
                    icon={<IconFlag className="size-4" />}
                    iconClassName="bg-brand-50 text-brand-600"
                    label="Feature flags"
                    value={stats?.total ?? '—'}
                    hint={stats && `${stats.beta} in beta · ${stats.dev} dev`}
                />
                <StatTile
                    icon={<IconTrendUp className="size-4" />}
                    iconClassName="bg-emerald-50 text-emerald-600"
                    label="Enabled by default"
                    value={stats?.enabled ?? '—'}
                    hint={stats && `of ${stats.total} flags`}
                />
                <StatTile
                    icon={<IconFlag className="size-4" />}
                    iconClassName="bg-blue-50 text-blue-600"
                    label="Rollout gates"
                    value={stats?.gated ?? '—'}
                    hint={stats && 'flags with a percentage gate'}
                />
                <StatTile
                    icon={<IconArchive className="size-4" />}
                    iconClassName="bg-amber-50 text-amber-600"
                    label="Archived"
                    value={stats?.archived ?? '—'}
                    hint={stats && 'never served, override or not'}
                />
            </div>

            <ComingSoonPanel
                icon={<IconAudit className="size-6" />}
                title="Recent activity"
                blurb="The cross-flag activity feed lands with the global audit log. Until then, each flag's full history lives on its detail page."
            />
        </>
    );
}

function countLifecycle(rows: readonly FlagListItem[], lifecycle: FlagLifecycle): number {
    return rows.filter((row) => row.definition.lifecycle === lifecycle).length;
}
